/**
 * One template page → its display list: placeholders resolved, text laid
 * out with real font metrics, viewports filled with their content and
 * clipped, scale bars / north arrows driven by their viewport's settings.
 */

import {
  PRINT_BLACK,
  PRINT_TEMPLATE_LIMITS,
  PRINT_WHITE,
  formatScale,
  manualLegendRows,
  lengthIn,
  pageSizeMm,
  paperMmToWorld,
  resolvePlaceholders,
  resolveTextStyle,
  textSizeToEmMm,
  worldToPaperMm,
  type GradientDefinition,
  type GradientType,
  type ImageSource,
  type LegendRow,
  type PrintLengthUnit,
  type LegendElement,
  type NorthArrowElement,
  type PlaceholderValues,
  type PrintColor,
  type PrintPageSpec,
  type PrintTemplateElement,
  type PrintTemplateSpec,
  type ScaleBarElement,
  type SheetRevision,
  type StrokeStyle,
  type TableElement,
  type TextStyleRef,
  type ViewportElement,
  type ViewportSettings,
} from 'shared-types';
import { ellipsePath, group, rectPath, roundedRectPath, type DisplayItem, type DisplayPage, type PathCommand, type TextItem } from './displayList';
import type { FontFace, FontResolver } from './fonts';
import { compose, rotate, translate, type Matrix } from './geometry';
import { drawingLegendRows } from './plan/dxfToDisplayList';
import { layoutTextBox, measureLine } from './text';

export interface ImageResource {
  /** Key the backends load the image under. */
  key: string;
  widthPx: number;
  heightPx: number;
}

export interface ViewportContent {
  settings: ViewportSettings;
  /** Viewport-local mm (0..w, 0..h); null = nothing to show yet (unframed / still rendering). */
  items: DisplayItem[] | null;
}

export interface PageLayoutInput {
  spec: PrintTemplateSpec;
  page: PrintPageSpec;
  /** Every placeholder value of this page (see placeholderValues). */
  values: PlaceholderValues;
  revisions: readonly SheetRevision[];
  date: Date;
  /** `marker` for the editor preview (unknown keys, empty viewports visible), `empty` for the export. */
  missing: 'empty' | 'marker';
  fonts: FontResolver;
  image: (source: ImageSource) => ImageResource | null;
  /** By viewport element id. */
  viewports: ReadonlyMap<string, ViewportContent>;
  /** The user's length unit for printed values: scale bars, legends (default meters). */
  units?: PrintLengthUnit;
  /** The sheet's own legend texts, by legend element id, then row key (see legendRows). */
  legendLabels?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** Heatmap gradients for legends (defined in the viewer, not in shared-types). */
  gradient?: (name: GradientType) => GradientDefinition | null;
}

const MARKER_FILL: PrintColor = { space: 'gray', k: 0.06 };
const MARKER_INK: PrintColor = { space: 'gray', k: 0.45 };

interface Ctx {
  input: PageLayoutInput;
  /** The rows each legend showed (handed back with the page). */
  legends: Record<string, LegendRow[]>;
}

// ─── Text helpers ───────────────────────────────────────────────────────

interface ResolvedStyle {
  face: FontFace;
  emMm: number;
  color: PrintColor;
  lineHeight: number;
  letterSpacingMm: number;
}

function styleOf(ctx: Ctx, ref: TextStyleRef): ResolvedStyle {
  const style = resolveTextStyle(ctx.input.spec, ref);
  const face = ctx.input.fonts.resolve(style.font, style.weight, style.italic);
  return { face, emMm: textSizeToEmMm(style.size, face.capHeightRatio), color: style.color, lineHeight: style.lineHeight, letterSpacingMm: style.letterSpacingMm };
}

function resolveText(ctx: Ctx, template: string, extra?: PlaceholderValues): string {
  const values = extra ? { ...ctx.input.values, ...extra } : ctx.input.values;
  return resolvePlaceholders(template, values, { date: ctx.input.date, missing: ctx.input.missing }).text;
}

interface BoxText {
  text: string;
  style: ResolvedStyle;
  x: number;
  y: number;
  w: number;
  h: number;
  align: 'left' | 'center' | 'right';
  valign: 'top' | 'middle' | 'bottom';
  autoShrink: boolean;
  paddingMm: number;
}

function textBox(box: BoxText): TextItem[] {
  if (!box.text) return [];
  const laid = layoutTextBox({
    text: box.text,
    face: box.style.face,
    emMm: box.style.emMm,
    lineHeight: box.style.lineHeight,
    letterSpacingMm: box.style.letterSpacingMm,
    widthMm: box.w,
    heightMm: box.h,
    paddingMm: box.paddingMm,
    align: box.align,
    valign: box.valign,
    autoShrink: box.autoShrink,
    minEmMm: PRINT_TEMPLATE_LIMITS.minTextEmMm,
  });
  return laid.lines
    .filter((line) => line.text)
    .map((line) => ({ type: 'text', x: box.x + line.x, y: box.y + line.y, text: line.text, font: box.style.face.key, sizeMm: laid.emMm, color: box.style.color, letterSpacingMm: box.style.letterSpacingMm, rotationDeg: 0 }));
}

/** A single line centered on `cx` with its baseline at `y`. */
function centeredLabel(text: string, style: ResolvedStyle, cx: number, y: number): TextItem {
  const width = measureLine(text, style.face, style.emMm, style.letterSpacingMm);
  return { type: 'text', x: cx - width / 2, y, text, font: style.face.key, sizeMm: style.emMm, color: style.color, letterSpacingMm: style.letterSpacingMm, rotationDeg: 0 };
}

function path(commands: PathCommand[], stroke: StrokeStyle | null, fill: PrintColor | null): DisplayItem {
  return { type: 'path', commands, stroke, fill, fillRule: 'nonzero' };
}

// ─── Elements ───────────────────────────────────────────────────────────

/** Page (or parent) → element box-local coordinates: origin at the box's top-left, rotated about its center. */
function elementMatrix(element: PrintTemplateElement): Matrix {
  const { xMm: x, yMm: y, wMm: w, hMm: h, rotationDeg } = element;
  if (!rotationDeg) return translate(x, y);
  return compose(translate(x + w / 2, y + h / 2), rotate(rotationDeg), translate(-w / 2, -h / 2));
}

function viewportSettingsOf(ctx: Ctx, viewportId: string | null): ViewportSettings | null {
  return viewportId === null ? null : (ctx.input.viewports.get(viewportId)?.settings ?? null);
}

function layoutViewport(ctx: Ctx, element: ViewportElement): DisplayItem[] {
  const { wMm: w, hMm: h } = element;
  const content = ctx.input.viewports.get(element.id);
  const out: DisplayItem[] = [];
  if (content?.items) {
    out.push(group(content.items, null, { x: 0, y: 0, w, h }));
  } else if (ctx.input.missing === 'marker') {
    const style = styleOf(ctx, {});
    const scaleDen = content?.settings.scaleDen ?? element.defaultScaleDen;
    out.push(path(rectPath(0, 0, w, h), null, MARKER_FILL));
    out.push(...textBox({ text: `${element.label || element.id}\n${formatScale(scaleDen)}`, style: { ...style, color: MARKER_INK }, x: 0, y: 0, w, h, align: 'center', valign: 'middle', autoShrink: true, paddingMm: 2 }));
  }
  if (element.frame) out.push(path(rectPath(0, 0, w, h), element.frame, null));
  return out;
}

/** Largest 1 / 2 / 5 × 10ⁿ meters not exceeding `max`. */
export function niceLength(max: number): number {
  if (!(max > 0)) return 0;
  const exponent = Math.floor(Math.log10(max));
  for (const step of [5, 2, 1]) {
    const value = step * 10 ** exponent;
    if (value <= max * (1 + 1e-9)) return value;
  }
  return 10 ** (exponent - 1) * 5;
}

/** A length label number (no unit), three decimals at most. */
function formatMeters(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

function layoutScaleBar(ctx: Ctx, element: ScaleBarElement): DisplayItem[] {
  const settings = viewportSettingsOf(ctx, element.viewportId);
  const linked = findViewport(ctx.input.page.elements, element.viewportId);
  const firstView = ctx.input.viewports.values().next().value?.settings.scaleDen ?? findFirstViewport(ctx.input.page.elements)?.defaultScaleDen;
  const scaleDen = settings?.scaleDen ?? linked?.defaultScaleDen ?? firstView;
  if (!scaleDen) return [];
  const style = styleOf(ctx, element.labelStyle);
  // Room for the centered end label inside the box.
  // A round length in the user's unit (geometry in meters).
  const unit = ctx.input.units ?? 'm';
  const usable = Math.max(0, element.wMm - measureLine(`00 ${unit}`, style.face, style.emMm, style.letterSpacingMm));
  const total = niceLength(lengthIn(paperMmToWorld(usable, scaleDen), unit));
  if (total <= 0) return [];
  const barLength = worldToPaperMm(total / lengthIn(1, unit), scaleDen);
  const segments = element.segments;
  const segmentMm = barLength / segments;
  const barH = Math.min(2.5, element.hMm * 0.35);
  const barY = element.hMm - barH;
  const out: DisplayItem[] = [];
  for (let i = 0; i < segments; i++) {
    out.push(path(rectPath(i * segmentMm, barY, segmentMm, barH), element.stroke, i % 2 === 0 ? element.fill : PRINT_WHITE));
  }
  const baseline = barY - Math.max(0.8, style.emMm * 0.25);
  for (let i = 0; i <= segments; i++) {
    const label = formatMeters((total / segments) * i) + (i === segments ? ` ${unit}` : '');
    out.push(centeredLabel(label, style, i * segmentMm, baseline));
  }
  return out;
}

function findFirstViewport(elements: readonly PrintTemplateElement[]): ViewportElement | null {
  for (const element of elements) {
    if (element.kind === 'viewport') return element;
    if (element.kind === 'group') {
      const inner = findFirstViewport(element.children);
      if (inner) return inner;
    }
  }
  return null;
}

function findViewport(elements: readonly PrintTemplateElement[], id: string | null): ViewportElement | null {
  if (id === null) return null;
  for (const element of elements) {
    if (element.kind === 'viewport' && element.id === id) return element;
    if (element.kind === 'group') {
      const inner = findViewport(element.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function layoutNorthArrow(ctx: Ctx, element: NorthArrowElement): DisplayItem[] {
  const style = styleOf(ctx, element.labelStyle);
  const labelH = style.face.capHeightRatio * style.emMm + 1;
  const size = Math.max(1, Math.min(element.wMm, element.hMm - labelH));
  const cx = element.wMm / 2;
  const cy = labelH + size / 2;
  const half = size / 2;
  const items: DisplayItem[] = [];
  const outline: PathCommand[] = [{ op: 'M', x: 0, y: -half }, { op: 'L', x: half * 0.6, y: half }, { op: 'L', x: 0, y: half * 0.5 }, { op: 'L', x: -half * 0.6, y: half }, { op: 'Z' }];
  switch (element.variant) {
    case 'triangle':
      items.push(path([{ op: 'M', x: 0, y: -half }, { op: 'L', x: half * 0.7, y: half }, { op: 'L', x: -half * 0.7, y: half }, { op: 'Z' }], element.stroke, element.fill));
      break;
    case 'compass': {
      items.push(path(ellipsePath(0, 0, half, half), element.stroke, null));
      const inner = 0.75;
      items.push(
        path(
          outline.map((c) => (c.op === 'Z' ? c : { ...c, x: c.x * inner, y: c.y * inner })),
          element.stroke,
          PRINT_WHITE,
        ),
      );
      items.push(path([{ op: 'M', x: 0, y: -half * inner }, { op: 'L', x: 0, y: half * 0.5 * inner }, { op: 'L', x: -half * 0.6 * inner, y: half * inner }, { op: 'Z' }], null, element.fill));
      break;
    }
    default:
      items.push(path(outline, element.stroke, PRINT_WHITE));
      // Classic half-filled arrow: the left half inked.
      items.push(path([{ op: 'M', x: 0, y: -half }, { op: 'L', x: 0, y: half * 0.5 }, { op: 'L', x: -half * 0.6, y: half }, { op: 'Z' }], null, element.fill));
  }
  items.push(centeredLabel('N', style, 0, -half - 1));
  // North is world +Y: on paper it turns clockwise by the viewport heading.
  const heading = viewportSettingsOf(ctx, element.viewportId)?.headingDeg ?? 0;
  return [group(items, compose(translate(cx, cy), rotate(heading)))];
}

function gradientColor(stops: GradientDefinition, t: number): PrintColor {
  const sorted = [...stops].sort((a, b) => a[0] - b[0]);
  if (sorted.length === 0) return PRINT_BLACK;
  if (t <= sorted[0][0]) return { space: 'rgb', ...sorted[0][1] };
  for (let i = 1; i < sorted.length; i++) {
    const [p1, c1] = sorted[i];
    const [p0, c0] = sorted[i - 1];
    if (t <= p1) {
      const f = p1 === p0 ? 0 : (t - p0) / (p1 - p0);
      return { space: 'rgb', r: c0.r + (c1.r - c0.r) * f, g: c0.g + (c1.g - c0.g) * f, b: c0.b + (c1.b - c0.b) * f };
    }
  }
  return { space: 'rgb', ...sorted[sorted.length - 1][1] };
}

function layoutLegend(ctx: Ctx, element: LegendElement): DisplayItem[] {
  const style = styleOf(ctx, element.style);
  const cap = style.face.capHeightRatio * style.emMm;
  const rowH = style.emMm * 1.6;
  const out: DisplayItem[] = [];
  let y = 0;
  if (element.title) {
    out.push(...textBox({ text: resolveText(ctx, element.title), style, x: 0, y: 0, w: element.wMm, h: rowH, align: 'left', valign: 'top', autoShrink: true, paddingMm: 0 }));
    y = rowH;
  }
  const settings = viewportSettingsOf(ctx, element.viewportId);
  const swatchW = Math.min(8, element.wMm * 0.3);
  const row = (label: string, swatch: DisplayItem) => {
    out.push(swatch);
    out.push(...textBox({ text: label, style, x: swatchW + 1.5, y, w: element.wMm - swatchW - 1.5, h: rowH, align: 'left', valign: 'middle', autoShrink: true, paddingMm: 0 }));
    y += rowH;
  };
  switch (element.source) {
    case 'manual':
    case 'layers': {
      // Manual items, or one row per color the plan prints; each row reads the sheet's own text when it has one.
      const labels = ctx.input.legendLabels?.[element.id] ?? {};
      const rows = element.source === 'manual' ? manualLegendRows(element) : drawingLegendRows(element.viewportId === null ? [] : (ctx.input.viewports.get(element.viewportId)?.items ?? []));
      ctx.legends[element.id] = rows;
      for (const legendRow of rows) {
        const own = labels[legendRow.key]?.trim();
        const label = resolveText(ctx, own || legendRow.defaultLabel);
        const swatch =
          legendRow.swatch === 'fill'
            ? path(rectPath(0, y + (rowH - cap) / 2, swatchW, cap), null, legendRow.color)
            : path(
                [
                  { op: 'M', x: 0, y: y + rowH / 2 },
                  { op: 'L', x: swatchW, y: y + rowH / 2 },
                ],
                { color: legendRow.color, weightMm: Math.max(0.25, legendRow.weightMm), dashMm: [], cap: 'butt', join: 'miter' },
                null,
              );
        row(label, swatch);
      }
      break;
    }
    case 'heatmap': {
      const heatmap = settings?.heatmap;
      const stops = heatmap ? ctx.input.gradient?.(heatmap.gradient) : null;
      if (!heatmap || !stops) break;
      const barW = Math.min(6, element.wMm * 0.25);
      const barH = element.hMm - y - cap;
      if (barH <= 0) break;
      const top = y + cap / 2;
      const strips = 64;
      for (let i = 0; i < strips; i++) {
        // Top of the bar is the maximum.
        const t = 1 - (i + 0.5) / strips;
        out.push(path(rectPath(0, top + (barH * i) / strips, barW, barH / strips + 0.01), null, gradientColor(stops, t)));
      }
      out.push(path(rectPath(0, top, barW, barH), { color: PRINT_BLACK, weightMm: 0.13, dashMm: [], cap: 'butt', join: 'miter' }, null));
      const ticks = 5;
      for (let i = 0; i < ticks; i++) {
        const f = i / (ticks - 1);
        const value = heatmap.max - (heatmap.max - heatmap.min) * f;
        const ty = top + barH * f;
        out.push(
          path(
            [
              { op: 'M', x: barW, y: ty },
              { op: 'L', x: barW + 1, y: ty },
            ],
            { color: PRINT_BLACK, weightMm: 0.13, dashMm: [], cap: 'butt', join: 'miter' },
            null,
          ),
        );
        out.push({ type: 'text', x: barW + 1.8, y: ty + cap / 2, text: formatMeters(lengthIn(value, ctx.input.units)), font: style.face.key, sizeMm: style.emMm, color: style.color, letterSpacingMm: style.letterSpacingMm, rotationDeg: 0 });
      }
      break;
    }
  }
  return out;
}

function layoutTable(ctx: Ctx, element: TableElement): DisplayItem[] {
  const headerStyle = styleOf(ctx, element.headerStyle);
  const cellStyle = styleOf(ctx, element.cellStyle);
  const rows: { cells: string[]; header: boolean }[] = [];
  if (element.showHeader) rows.push({ header: true, cells: element.columns.map((c) => resolveText(ctx, c.header)) });
  if (element.rows.kind === 'revisions') {
    const ordered = element.rows.newestFirst ? [...ctx.input.revisions].reverse() : [...ctx.input.revisions];
    for (let i = 0; i < element.rows.maxRows; i++) {
      const revision = ordered[i];
      const values: PlaceholderValues | null = revision ? { 'rev.rev': revision.rev, 'rev.date': revision.date, 'rev.description': revision.description, 'rev.by': revision.by } : null;
      rows.push({ header: false, cells: element.columns.map((c) => (values ? resolveText(ctx, c.cell, values) : '')) });
    }
  } else {
    for (const cells of element.rows.cells) rows.push({ header: false, cells: element.columns.map((_, j) => resolveText(ctx, cells[j] ?? '')) });
  }
  // Rows share the box's height (the row height is what they get when the box was sized for them).
  const rowH = rows.length > 0 && element.hMm > 0 ? element.hMm / rows.length : element.rowHeightMm;
  const out: DisplayItem[] = [];
  // The table fills its box: columns keep their proportions, rows share the height.
  const naturalW = element.columns.reduce((sum, c) => sum + c.widthMm, 0);
  const scaleX = naturalW > 0 && element.wMm > 0 ? element.wMm / naturalW : 1;
  const widths = element.columns.map((c) => c.widthMm * scaleX);
  const totalW = widths.reduce((sum, w) => sum + w, 0);
  const rowY = (i: number) => (element.growUp ? element.hMm - rowH * (i + 1) : rowH * i);
  rows.forEach((row, i) => {
    let x = 0;
    row.cells.forEach((cell, j) => {
      const column = element.columns[j];
      out.push(...textBox({ text: cell, style: row.header ? headerStyle : cellStyle, x, y: rowY(i), w: widths[j], h: rowH, align: column.align, valign: 'middle', autoShrink: true, paddingMm: 1 }));
      x += widths[j];
    });
  });
  if (element.stroke && rows.length > 0) {
    const top = Math.min(rowY(0), rowY(rows.length - 1));
    const height = rowH * rows.length;
    const grid: PathCommand[] = [...rectPath(0, top, totalW, height)];
    for (let i = 1; i < rows.length; i++) grid.push({ op: 'M', x: 0, y: top + rowH * i }, { op: 'L', x: totalW, y: top + rowH * i });
    let x = 0;
    for (let j = 0; j < element.columns.length - 1; j++) {
      x += widths[j];
      grid.push({ op: 'M', x, y: top }, { op: 'L', x, y: top + height });
    }
    out.push(path(grid, element.stroke, null));
  }
  return out;
}

function layoutImage(ctx: Ctx, element: Extract<PrintTemplateElement, { kind: 'image' }>): DisplayItem[] {
  const resource = ctx.input.image(element.source);
  const { wMm: w, hMm: h } = element;
  if (!resource) {
    return ctx.input.missing === 'marker' ? [path(rectPath(0, 0, w, h), { color: MARKER_INK, weightMm: 0.13, dashMm: [1, 1], cap: 'butt', join: 'miter' }, MARKER_FILL)] : [];
  }
  if (element.fit === 'stretch' || resource.widthPx <= 0 || resource.heightPx <= 0) return [{ type: 'image', image: resource.key, x: 0, y: 0, w, h }];
  const aspect = resource.widthPx / resource.heightPx;
  const fitByWidth = element.fit === 'contain' ? w / h < aspect : w / h > aspect;
  const iw = fitByWidth ? w : h * aspect;
  const ih = fitByWidth ? w / aspect : h;
  // Placed in the box by its alignment (center / middle by default).
  const fx = element.align === 'left' ? 0 : element.align === 'right' ? 1 : 0.5;
  const fy = element.valign === 'top' ? 0 : element.valign === 'bottom' ? 1 : 0.5;
  const image: DisplayItem = { type: 'image', image: resource.key, x: (w - iw) * fx, y: (h - ih) * fy, w: iw, h: ih };
  return element.fit === 'cover' ? [group([image], null, { x: 0, y: 0, w, h })] : [image];
}

function layoutElement(ctx: Ctx, element: PrintTemplateElement): DisplayItem | null {
  if (element.hidden) return null;
  const { wMm: w, hMm: h } = element;
  let items: DisplayItem[];
  switch (element.kind) {
    case 'text': {
      const style = styleOf(ctx, element.style);
      items = textBox({ text: resolveText(ctx, element.content), style, x: 0, y: 0, w, h, align: element.align, valign: element.valign, autoShrink: element.autoShrink, paddingMm: element.paddingMm });
      break;
    }
    case 'image':
      items = layoutImage(ctx, element);
      break;
    case 'rect':
      items = element.stroke || element.fill ? [path(roundedRectPath(0, 0, w, h, element.cornerRadiusMm), element.stroke, element.fill)] : [];
      break;
    case 'ellipse':
      items = element.stroke || element.fill ? [path(ellipsePath(w / 2, h / 2, w / 2, h / 2), element.stroke, element.fill)] : [];
      break;
    case 'polyline': {
      const commands: PathCommand[] = element.pointsMm.map(([x, y], i) => (i === 0 ? { op: 'M', x, y } : { op: 'L', x, y }));
      if (element.closed) commands.push({ op: 'Z' });
      items = element.stroke || element.fill ? [path(commands, element.stroke, element.closed ? element.fill : null)] : [];
      break;
    }
    case 'viewport':
      items = layoutViewport(ctx, element);
      break;
    case 'scaleBar':
      items = layoutScaleBar(ctx, element);
      break;
    case 'northArrow':
      items = layoutNorthArrow(ctx, element);
      break;
    case 'legend':
      items = layoutLegend(ctx, element);
      break;
    case 'table':
      items = layoutTable(ctx, element);
      break;
    case 'group':
      items = element.children.map((child) => layoutElement(ctx, child)).filter((item): item is DisplayItem => item !== null);
      break;
  }
  return items.length > 0 ? group(items, elementMatrix(element)) : null;
}

export function layoutPage(input: PageLayoutInput): DisplayPage {
  const ctx: Ctx = { input, legends: {} };
  const { widthMm, heightMm } = pageSizeMm(input.page.paper, input.page.orientation);
  const items = input.page.elements.map((element) => layoutElement(ctx, element)).filter((item): item is DisplayItem => item !== null);
  return { widthMm, heightMm, items, legends: ctx.legends };
}
